import { useState } from "react";
import { useIntl } from "react-intl";
import type {
  AvatarRejection,
  CropResult,
} from "@/components/common/AvatarCropper";
import { acceptsFile } from "@/lib/file-ingest";
import { LocalizedError } from "@/lib/localized-error";
import {
  MAX_AVATAR_PIXELS,
  MAX_COMPANY_LOGO_BYTES,
  MB,
  RASTER_IMAGE_ACCEPT,
} from "@/lib/upload-limits";
import type { CompanyLogoValue } from "./company-logo";
import type {
  CompanyLogoInput,
  UseCompanyAdminResult,
} from "./use-company-admin";

// Uploading, replacing and removing the company's logo, WITHOUT a section of its own.
//
// This used to be CompanyLogoEditor: a dashed drop zone appended below /c/:id, drawing a second,
// square-cropped copy of the very logo the identity header was already showing 200 pixels above it.
// The zone is gone. The logo in the header IS the control now — click it to upload or replace — and
// what the zone KNEW is here.
//
// ITS OWN WRITE, STILL, and never a field on the profile save. genesis reads an update off the
// properties the request carried, so the two must not share a submit: a profile save that mentioned
// `logo` at all would either resend the whole image on every name change or, sending null, delete a
// logo the admin never touched. use-company-admin keeps `logo` out of saveProfile and everything
// else out of saveLogo; this hook is why that separation is still observable from the page.
//
// GENESIS IS THE AUTHORITY ON WHAT AN IMAGE IS. It caps at 5 MB and decides the format on the
// BYTES — magic numbers, SVG refused outright — because the mutation is reachable with a token and
// no browser. The size and type checks below are a courtesy that saves a doomed upload; they are
// never described to the admin as the rule, and a genesis refusal is surfaced as itself.
//
// A CHOSEN FILE IS NOT A PICK YET: IT IS CROPPED FIRST. Choosing hands the raw file to the same
// AvatarCropper a person's photo goes through, and only what comes back out of it is staged. Before
// that, the logo the admin had to look at was whatever came off their disk — a wide banner, a
// screenshot with white margins — squeezed into a round eighty-pixel frame by object-fit alone, with
// no way to say which part of it mattered. The two controls now do the same thing, which is what was
// asked for (Kevin, 2026-08-28).
//
// The size and type checks below still run BEFORE the cropper opens: a file that can never be a logo
// is refused by name straight away rather than after the admin has framed it.
//
// A PICKED FILE IS UNSAVED WORK AND SAYS SO. Choosing an image reaches genesis only when the pick is
// saved, and navigating, closing the tab and changing the acting-company context (@adr SPARK-0034-acting-company-context-resets-the-apollo-cache@3 empties
// the Apollo cache, which remounts the page) all destroy it. All three read the PendingEdits
// registry, so the caller registers COMPANY_SCOPE_LOGO around this. Nothing else in here is a
// draft: removing writes immediately.
//
// REMOVAL STAYS A TWO-STEP (@adr SPARK-0011-destructive-actions-require-confirmation@1): no window.confirm, and no first-click mutation. `askToRemove`
// only arms the question; `remove` is what writes. The blast radius is this one image and uploading
// again restores it, which is the case the ADR permits an inline two-step for.

/** The state and the operations the page needs to manage a logo from the identity header. */
export interface CompanyLogoEditing {
  /**
   * What the header should DRAW right now: the pick if there is one, otherwise the stored logo,
   * otherwise nothing (and the monogram takes over). `picked` and `removed` override the stored
   * value until the refetched query catches up, so the page never briefly shows the image that was
   * just replaced or dropped.
   */
  shown: CompanyLogoValue | null;
  /**
   * The bytes the admin has chosen and not yet written, if any.
   *
   * Exposed for ONE caller: the create gate, which folds the logo into `createCompany` rather than
   * writing it afterwards (@adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2) — so on a company that does not exist yet there is no second
   * request and no moment in which the company is stored without its logo. Everywhere else the pick
   * stays this hook's own business: `shown` is what a renderer wants, `saveOrThrow` what a flush
   * does.
   */
  picked: CompanyLogoInput | null;
  /**
   * The raw file the cropper is holding, or null when no crop is in progress. Nothing is staged and
   * nothing is unsaved while this is set: a file becomes a pick only through {@link applyCrop}.
   */
  cropFile: File | null;
  /** True while a picked file has not reached genesis — what the SaveBar reads. */
  unsaved: boolean;
  /** True for as long as the last upload is the last thing that happened. */
  pickedSaved: boolean;
  /** True once removal has been ASKED for and not yet confirmed. Nothing is written in this state. */
  confirmingRemove: boolean;
  /** A refusal, already localized: either this side's courtesy checks or genesis's own answer. */
  message: string | null;
  /** genesis's cap, in whole MB, for the hint beside the control. */
  maxMb: number;
  /** Refuses the doomed files by name, and opens the cropper on everything else. */
  choose: (files: FileList) => void;
  /** Stages the cropped square as the pick. What the admin framed is what genesis gets. */
  applyCrop: (result: CropResult) => void;
  /** Closes the cropper and stages nothing. The previous logo, picked or stored, stands. */
  cancelCrop: () => void;
  /** Closes the cropper and says why, for a file the cropper itself could not use. */
  rejectCrop: (reason: AvatarRejection) => void;
  askToRemove: () => void;
  cancelRemove: () => void;
  remove: () => Promise<void>;
  saveOrThrow: () => Promise<void>;
  discard: () => void;
  /**
   * Everything `discard` drops, PLUS the "already removed" flag — what unmounting the deleted
   * editor section used to do, for the caller that leaves edit mode.
   *
   * Not folded into `discard`, which the SaveBar and the guards call: that one must NOT forget a
   * removal genesis has already been told about, or the page would put the deleted logo back on
   * screen for as long as the refetch takes.
   */
  reset: () => void;
}

export function useCompanyLogoEditing(
  /** The stored logo as the page queried it, or null when the company has none. */
  logo: CompanyLogoValue | null | undefined,
  admin: UseCompanyAdminResult,
): CompanyLogoEditing {
  const intl = useIntl();
  const [picked, setPicked] = useState<CompanyLogoInput | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [pickedSaved, setPickedSaved] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const maxMb = Math.floor(MAX_COMPANY_LOGO_BYTES / MB);
  const shown = picked ?? (removed ? null : (logo ?? null));
  const unsaved = picked !== null && !pickedSaved;

  function choose(files: FileList) {
    setConfirmingRemove(false);
    const file = files[0];
    // A cancelled dialog and a drop that carried only a directory both arrive as an empty list. That
    // is neither a refusal nor a choice, so every piece of state — a standing refusal included —
    // stays exactly as it was.
    if (!file) return;
    // Both checks bite before the cropper mounts, so a file that could never be a logo is refused by
    // its own name rather than after the admin has spent a moment framing it. The bytes are not read
    // here at all any more: the cropper decodes the file and re-encodes what it draws.
    if (file.size > MAX_COMPANY_LOGO_BYTES) {
      setMessage(
        intl.formatMessage(
          {
            id: "company.admin.logo.tooLarge",
            defaultMessage:
              "{name} is too large. A logo may be at most {max, number} MB.",
          },
          { max: maxMb, name: file.name },
        ),
      );
      return;
    }
    if (!acceptsFile(file, RASTER_IMAGE_ACCEPT)) {
      setMessage(
        intl.formatMessage(
          {
            id: "company.admin.logo.unsupported",
            defaultMessage:
              "{name} cannot be used as a logo. Choose a PNG, JPEG, GIF, BMP, TIFF or WebP image.",
          },
          { name: file.name },
        ),
      );
      return;
    }
    setMessage(null);
    setCropFile(file);
  }

  function applyCrop({ mimeType, content }: CropResult) {
    // The cropper always hands back a JPEG, so the chosen file's own name would advertise a format
    // these bytes are not: genesis stores fileName as given and hands it back on download.
    const base = cropFile?.name.replace(/\.[^.]+$/, "") || "logo";
    setCropFile(null);
    setMessage(null);
    setRemoved(false);
    setPickedSaved(false);
    setPicked({ fileName: `${base}.jpg`, mimeType, content });
  }

  function cancelCrop() {
    setCropFile(null);
  }

  function rejectCrop(reason: AvatarRejection) {
    // CLOSE FIRST, THEN SPEAK. The refusal is printed under the company card, and a message left
    // behind an open modal is a message nobody reads.
    setCropFile(null);
    setMessage(
      reason === "pixels"
        ? intl.formatMessage(
            {
              id: "company.admin.logo.tooManyPixels",
              defaultMessage:
                "This image is too large to crop. Use one under {max} megapixels.",
            },
            { max: Math.round(MAX_AVATAR_PIXELS / 1_000_000) },
          )
        : // A file that decodes to nothing is a renamed non-image or a corrupt one: the type check
          // above only ever saw what the browser CLAIMED the file was.
          intl.formatMessage({
            id: "company.admin.logo.unreadable",
            defaultMessage: "That file could not be read. Try choosing it again.",
          }),
    );
  }

  async function save(): Promise<
    { ok: true } | { ok: false; message: string }
  > {
    if (!picked) return { ok: true };
    const result = await admin.saveLogo(picked);
    if (result.ok) {
      setPickedSaved(true);
      setMessage(null);
      return { ok: true };
    }
    setMessage(result.message);
    return result;
  }

  /**
   * @throws when genesis refuses the image. Whoever is holding a navigation reads a rejection as
   *   "do not proceed": letting it through on a refused upload would throw away the very file the
   *   reader chose to keep, and the browser cannot pick it up again on their behalf.
   */
  async function saveOrThrow(): Promise<void> {
    const result = await save();
    if (!result.ok) throw new LocalizedError(result.message);
  }

  function discard() {
    setPicked(null);
    setPickedSaved(false);
    setMessage(null);
    // An open cropper is a chosen file that has not been staged yet, and it is exactly as unwanted
    // after a discard as the pick is. Leaving it would put the dialog back over a page that has
    // just thrown its edits away.
    setCropFile(null);
  }

  function reset() {
    discard();
    setConfirmingRemove(false);
    // `removed` means "genesis has been told and this page's query has not caught up". Leaving edit
    // mode ends that story: keeping the flag would hide a logo somebody else uploaded in the
    // meantime, which the refetch would then be unable to show.
    setRemoved(false);
  }

  function askToRemove() {
    setConfirmingRemove(true);
  }

  function cancelRemove() {
    setConfirmingRemove(false);
  }

  async function remove() {
    setConfirmingRemove(false);
    const result = await admin.removeLogo();
    if (result.ok) {
      setPicked(null);
      setPickedSaved(false);
      setRemoved(true);
      setMessage(null);
    } else {
      // `removed` stays false, or the page would claim the monogram fallback for a logo genesis
      // still holds and the admin would believe the removal worked.
      setMessage(result.message);
    }
  }

  return {
    shown,
    picked,
    cropFile,
    unsaved,
    pickedSaved,
    confirmingRemove,
    message,
    maxMb,
    choose,
    applyCrop,
    cancelCrop,
    rejectCrop,
    askToRemove,
    cancelRemove,
    remove,
    saveOrThrow,
    discard,
    reset,
  };
}
